import api from "api/axios";

export type LeaveType = "annual" | "sick" | "casual" | "unpaid" | "other";
export type LeaveStatus = "pending" | "approved" | "rejected";

export interface LeaveTypeOption {
  value: LeaveType;
  label: string;
}

export const LEAVE_TYPE_OPTIONS: LeaveTypeOption[] = [
  { value: "annual", label: "Annual Leave" },
  { value: "sick", label: "Sick Leave" },
  { value: "casual", label: "Casual Leave" },
  { value: "unpaid", label: "Unpaid Leave" },
  { value: "other", label: "Other" },
];

// Single shared source for every display site (AttendancePage,
// AttendanceDayDetailModal, LeaveRequestsPanel) instead of each
// recomputing its own copy of the same value->label map.
export const LEAVE_TYPE_LABELS: Record<string, string> = Object.fromEntries(LEAVE_TYPE_OPTIONS.map((o) => [o.value, o.label]));

export interface LeaveRequestItem {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_code: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  reason: string;
  status: LeaveStatus;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_note: string | null;
}

interface LeaveListResponse {
  items: LeaveRequestItem[];
  total_count: number;
  page: number;
  total_pages: number;
}

export async function submitLeaveRequest(body: {
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  reason: string;
}): Promise<LeaveRequestItem> {
  const res = await api.post("/employee/leave-requests", body);
  return res.data;
}

export async function fetchMyLeaveRequests(status?: string, page = 1, pageSize = 50): Promise<LeaveListResponse> {
  const params: Record<string, any> = { page, page_size: pageSize };
  if (status) params.status = status;
  const res = await api.get("/employee/leave-requests", { params });
  return res.data;
}

export async function cancelLeaveRequest(id: number) {
  const res = await api.delete(`/employee/leave-requests/${id}`);
  return res.data;
}

// Admin-facing — written now (typed wrappers are cheap), wired into the UI
// in Phase 4 (LeaveRequestsPanel's "admin" role, LeaveRequestModal's
// "admin-direct" writeMode). No pagination UI yet, so pageSize defaults to
// the backend's max (200, see leave.py's Query(le=200)) rather than 50 —
// org-wide, the admin view can plausibly exceed 50 pending requests.
export async function fetchAllLeaveRequests(status?: string, employeeCode?: string, page = 1, pageSize = 200): Promise<LeaveListResponse> {
  const params: Record<string, any> = { page, page_size: pageSize };
  if (status) params.status = status;
  if (employeeCode) params.employee_code = employeeCode;
  const res = await api.get("/admin/leave-requests", { params });
  return res.data;
}

export async function adminCreateLeaveRequest(body: {
  employee_code: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  reason: string;
  note?: string;
}): Promise<LeaveRequestItem> {
  const res = await api.post("/admin/leave-requests", body);
  return res.data;
}

export async function reviewLeaveRequest(id: number, decision: "approve" | "reject", reviewNote?: string): Promise<LeaveRequestItem> {
  const res = await api.patch(`/admin/leave-requests/${id}`, { decision, review_note: reviewNote });
  return res.data;
}
